import { SupabaseClient } from '@supabase/supabase-js';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';

import { Database, withLogs } from '@core';

export type SwingThread = {
    id: number;
    swingUuid: string;
    fromFrame: number;
    linkNodeId: string | null;
    createdAt: string;
    updatedAt: string | null;
    createdBy: string;
    isDeleted: boolean;
};

export type SwingThreadWithTitle = SwingThread;

export type SwingThreadPayload = Pick<SwingThread, 'swingUuid' | 'fromFrame' | 'linkNodeId'> &
    Partial<Pick<SwingThread, 'createdBy' | 'id'>>;
export type SwingThreadDeletePayload = Pick<SwingThread, 'id'>;

export function assertHasCreatedBy(
    payload: SwingThreadPayload,
): asserts payload is Required<Pick<SwingThreadPayload, 'createdBy'>> &
    Pick<SwingThread, 'swingUuid' | 'fromFrame' | 'linkNodeId'> {
    if (!payload.createdBy) {
        throw new Error('createdBy not set — no user id provided.');
    }
}

const SWING_THREADS_BY_STUDENT_IDS = `
      id,
      swings:swing_uuid!inner (
        activities!inner (
          user_id
        )
      )`;

export const getSwingThreadByStudentIds = async (
    supabase: SupabaseClient<Database>,
    studentIds: string[],
): Promise<Record<string, boolean>> => {
    if (studentIds.length === 0) return {};

    const { data, error } = await supabase
        .from('swing_threads')
        .select(SWING_THREADS_BY_STUDENT_IDS)
        .eq('is_deleted', false)
        .in('swings.activities.user_id', studentIds);

    if (error) throw error;

    const owners = new Set((data ?? []).map((row) => row.swings.activities.user_id));

    return studentIds.reduce<Record<string, boolean>>((acc, id) => {
        acc[id] = owners.has(id);
        return acc;
    }, {});
};

const SWING_THREADS_BY_ENTITY = `
      id,
      swing_uuid,
      is_deleted,      
      swings:swing_uuid!inner (
        activity_id,
        activities!inner (
            id,
            user_id
        )
      )`;

export const getSwingThreadIdsByEntity = async (
    supabaseClient: SupabaseClient<Database>,
    activityId?: number,
    swingUuid?: string,
): Promise<number[]> => {
    let query = supabaseClient.from('swing_threads').select(SWING_THREADS_BY_ENTITY).eq('is_deleted', false);

    if (activityId) {
        query = query.eq('swings.activity_id', activityId);
    }
    if (swingUuid) {
        query = query.eq('swing_uuid', swingUuid);
    }

    const { data, error } = await query;

    if (error) throw error;

    return (data ?? []).map((row) => row.id);
};

export const getSwingThreads = withLogs(
    async (supabaseClient: SupabaseClient<Database>, swingUuid: string): Promise<SwingThreadWithTitle[]> => {
        const { data: threads, error } = await supabaseClient
            .from('swing_threads')
            .select('*')
            .eq('is_deleted', false)
            .eq('swing_uuid', swingUuid)
            .order('created_at', { ascending: true });

        if (error) {
            throw new Error(`Failed to fetch swing threads: ${error.message}`);
        }

        if (!threads || threads.length === 0) return [];

        const enriched = threads.map((thread) => {
            return camelcaseKeys({
                ...thread,
            });
        });

        return enriched;
    },
    'getSwingThreads',
);

export const upsertSwingThread = withLogs(
    async (supabaseClient: SupabaseClient<Database>, payload: SwingThreadPayload): Promise<SwingThread | null> => {
        assertHasCreatedBy(payload);

        const { data, error } = await supabaseClient
            .from('swing_threads')
            .upsert(snakecaseKeys(payload), { onConflict: 'id' })
            .select()
            .single();

        if (error) throw error;

        return data ? camelcaseKeys(data) : null;
    },
    'upsertSwingThread',
);

export const softDeleteSwingThread = withLogs(
    async (supabaseClient: SupabaseClient<Database>, payload: Pick<SwingThreadPayload, 'id'>): Promise<boolean> => {
        const { id } = payload;

        if (!id) {
            throw new Error('Thread ID is required for soft delete.');
        }
        const { error: msgError } = await supabaseClient
            .from('swing_thread_messages')
            .update({ is_deleted: true, updated_at: new Date().toISOString() })
            .eq('thread_id', id);

        if (msgError) {
            throw new Error(`Failed to soft delete thread messages: ${msgError.message}`);
        }
        const { error } = await supabaseClient
            .from('swing_threads')
            .update({ is_deleted: true, updated_at: new Date().toISOString() })
            .eq('id', id);

        if (error) {
            throw new Error(`Failed to soft delete swing thread: ${error.message}`);
        }

        return true;
    },
    'softDeleteSwingThread',
);

export function subscribeToSwingThreads(
    supabaseClient: SupabaseClient<Database>,
    swingUuid: string,
    onChange: (payload: any) => void,
) {
    return supabaseClient
        .channel(`swing_threads_${swingUuid}`)
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'swing_threads',
                filter: `swing_uuid=eq.${swingUuid}`,
            },
            (payload) => {
                onChange(payload);
            },
        )
        .subscribe((status) => {
            if (status === 'CHANNEL_ERROR') {
                console.error('Swing thread subscription error:', status);
            }
        });
}
